import {Alert, Modal, Tag, Tooltip} from "antd";
import styled from "styled-components";
import {CheckCircleOutlined, CopyOutlined, DeleteOutlined, EditOutlined, LineChartOutlined, QuestionCircleOutlined, StopOutlined} from "@ant-design/icons";
import {useNavigate} from "react-router-dom";
import {Image, HighlighterCircle, ArrowFatLineUp, EnvelopeSimple, UserFocus} from "@phosphor-icons/react";
import {IJobDemand, IJobSubscription} from "../../../services/types.ts";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {deleteJobDemand, duplicateJobDemand, updateJobDemand} from "../../../services/api";
import {FC, memo, ReactNode, useContext, useState} from "react";
import {CancelSubscriptionModal} from "../../jobSubscriptions/CancelSubscriptionModal.tsx";
import {BtnStyledMessageItem, ButtonGroupMessageItem} from "candidate/src/pages/messages/components/MessageItem.tsx";
import {
    BgWrapper,
    ItemDetails,
    RowItemWrapper
} from "candidate/src/pages/jobPool/components/JobPoolItem.tsx";
import {useMediaQuery} from "@uidotdev/usehooks";
import {AuthContext} from "../../../context/AuthContext.tsx";
interface JobItemProps {
    item: IJobDemand;
    onEdit: () => void;
    subscription?: IJobSubscription;
}
interface JobAlertProps {
    isActive: boolean;
    pricingModel?: string;
}

const ADDON_CONFIG: Record<string, { label: string; icon: ReactNode; color: string }> = {
    has_logo: {label: 'Logo', icon: <Image size={14} weight="bold"/>, color: 'blue'},
    has_highlight: {label: 'Hervorgehoben', icon: <HighlighterCircle size={14} weight="bold"/>, color: 'orange'},
    has_top_position: {label: 'Top-Anzeige', icon: <ArrowFatLineUp size={14} weight="bold"/>, color: 'gold'},
    has_email_distribution: {label: 'E-Mail-Verteilung', icon: <EnvelopeSimple size={14} weight="bold"/>, color: 'purple'},
    has_direct_contact: {label: 'Direktansprache', icon: <UserFocus size={14} weight="bold"/>, color: 'cyan'},
};

const CATEGORY_LABELS: Record<string, string> = {
    fachkraefte: 'Fachkräfte',
    hilfskraefte: 'Hilfskräfte',
};

const formatDate = (timestamp: number | null | undefined) => {
    if (!timestamp) return null;
    return new Date(timestamp).toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    });
};

export const JobItem: FC<JobItemProps> = memo(({item, onEdit, subscription}) => {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const isMobile = useMediaQuery("only screen and (max-width : 768px)");
    const {user} = useContext(AuthContext);

    const isPerJobPricing = item.pricing_model === 'per_job';
    const [isCancelModalVisible, setIsCancelModalVisible] = useState(false);

    // Legacy listings use job_demand_status_id 4 for "published". Per-job ads often stay on 3 while the API sets status_label to "aktiv".
    const isJobActive = isPerJobPricing
        ? item.job_demand_status_id === 4 || item.status_label?.toLowerCase().trim() === 'aktiv'
        : item.job_demand_status_id === 4;

    const deleteMutation = useMutation({
        mutationFn: (id: string) => deleteJobDemand(id),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['jobs']});
            queryClient.invalidateQueries({queryKey: ['user']});
        },
    });

    const duplicateMutation = useMutation({
        mutationFn: (id: string) => duplicateJobDemand(id),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['jobs']});
            queryClient.invalidateQueries({queryKey: ['user']});
        },
    });

    const publishMutation = useMutation({
        mutationFn: (newStatus: number) => updateJobDemand(item.id!.toString(), { job_demand_status_id: newStatus }),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['jobs']});
            queryClient.invalidateQueries({queryKey: ['user']});
        },
    });

    const canPublish = (user?.plan_type === "paid" && !user?._client?.isPrivate && ((user?.jobs_current_month ?? 0) < (user?.jobs_allowed_monthly ?? 0)))
        || ((user?.jobs_current_month ?? 0) < (user?.privateJobsAllowed ?? 0));

    const handleDelete = () => {
        if (!item?.id) return;
        Modal.confirm({
            title: "Möchten Sie diesen Job wirklich löschen?",
            content: "Diese Aktion kann nicht rückgängig gemacht werden.",
            okText: "Ja",
            okType: "danger",
            cancelText: "Nein",
            onOk() {
                if (item.id) {
                    deleteMutation.mutate(item.id.toString());
                }
            },
        });
    };

    const handleDuplicate = () => {
        if (!item?.id) return;
        duplicateMutation.mutate(item.id.toString());
    };

    // Get active addons for per-job pricing items
    const getActiveAddonTags = () => {
        const tags: string[] = [];
        if (item.has_logo) tags.push('has_logo');
        if (item.has_highlight) tags.push('has_highlight');
        if (item.has_top_position) tags.push('has_top_position');
        if (item.has_email_distribution) tags.push('has_email_distribution');
        if (item.has_direct_contact) tags.push('has_direct_contact');
        return tags;
    };

    // Per-job pricing rendering
    if (isPerJobPricing) {
        const jobCategory = item.job_category;
        const activeAddons = getActiveAddonTags();

        return (
            <RowItemWrapper>
                <BgWrapper style={{marginBottom: isMobile ? 12 : 0}}>
                    <ItemDetails style={{width: "100%"}}>
                        <JobMetaWrapper>
                            <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                                <JobTitle>{item.title ?? 'Job Title'}</JobTitle>
                                <Tag color={isJobActive ? 'green' : 'red'}>
                                    {isJobActive ? 'aktiv' : 'inaktiv'}
                                </Tag>
                                {isJobActive && subscription?.status === 'canceled' && subscription?.expiration_date && (
                                    <Tag color="orange">bis {formatDate(subscription.expiration_date)}</Tag>
                                )}
                            </div>
                            <JobLocation>Standort: {item.search_location_label ?? 'N/A'}</JobLocation>
                            <AddonTagsWrapper>
                                {jobCategory && (
                                    <Tag color="blue" style={{fontSize: 11, display: 'flex', alignItems: 'center', gap: 4}}>
                                        {CATEGORY_LABELS[jobCategory] ?? jobCategory}
                                    </Tag>
                                )}
                                {activeAddons.map(key => {
                                    const config = ADDON_CONFIG[key];
                                    return (
                                        <Tooltip key={key} title={config?.label ?? key}>
                                            <Tag color="blue" style={{fontSize: 11, display: 'flex', alignItems: 'center', gap: 4}}>
                                                {config?.icon}
                                                {config?.label ?? key}
                                            </Tag>
                                        </Tooltip>
                                    );
                                })}
                            </AddonTagsWrapper>
                        </JobMetaWrapper>
                        <ButtonGroupMessageItem>
                            {!isJobActive && (
                                <BtnStyledMessageItem type="primary" size="small" onClick={() => navigate(`/new/job?draftId=${item.id}`)} icon={<CheckCircleOutlined />}>
                                    Aktivieren
                                </BtnStyledMessageItem>
                            )}
                            <BtnStyledMessageItem size="small" onClick={onEdit} icon={<EditOutlined />}>Stelle bearbeiten</BtnStyledMessageItem>
                            <BtnStyledMessageItem size="small" onClick={handleDuplicate}
                                                  loading={duplicateMutation.isPending}
                                                  icon={<CopyOutlined />}>Duplizieren</BtnStyledMessageItem>
                            {isJobActive && subscription && subscription.status !== 'canceled' && (
                                <BtnStyledMessageItem size="small" onClick={() => setIsCancelModalVisible(true)} loading={publishMutation.isPending} icon={<StopOutlined />}>Deaktivieren</BtnStyledMessageItem>
                            )}
                            {isJobActive && (!subscription || subscription.status === 'canceled') && (
                                <BtnStyledMessageItem size="small" onClick={() => publishMutation.mutate(3)} loading={publishMutation.isPending} icon={<StopOutlined />}>Sofort deaktivieren</BtnStyledMessageItem>
                            )}
                            <BtnStyledMessageItem size="small" danger onClick={handleDelete}
                                                  loading={deleteMutation.isPending}
                                                  icon={<DeleteOutlined />}>Löschen</BtnStyledMessageItem>
                        </ButtonGroupMessageItem>
                    </ItemDetails>
                </BgWrapper>
                {subscription && (
                    <CancelSubscriptionModal
                        subscription={subscription}
                        open={isCancelModalVisible}
                        onClose={() => setIsCancelModalVisible(false)}
                        onSuccess={() => {
                            setIsCancelModalVisible(false);
                            queryClient.invalidateQueries({queryKey: ['jobs']});
                            queryClient.invalidateQueries({queryKey: ['jobSubscriptions']});
                            queryClient.invalidateQueries({queryKey: ['user']});
                        }}
                    />
                )}
            </RowItemWrapper>
        );
    }

    const legacyActiveAddons = getActiveAddonTags();

    // Legacy rendering
    return (
        <RowItemWrapper>
            <BgWrapper style={{marginBottom: isMobile ? 12 : 0}}>
                <ItemDetails style={{width: "100%"}}>
                    <JobMetaWrapper>
                        <JobTitle>{item.title ?? 'Job Title'}</JobTitle>
                        <JobLocation>Standort: {item.search_location_label ?? 'N/A'}</JobLocation>
                        {legacyActiveAddons.length > 0 && (
                            <AddonTagsWrapper>
                                {legacyActiveAddons.map(key => {
                                    const config = ADDON_CONFIG[key];
                                    return (
                                        <Tooltip key={key} title={config?.label ?? key}>
                                            <Tag color={config?.color ?? 'purple'} style={{fontSize: 11, display: 'flex', alignItems: 'center', gap: 4}}>
                                                {config?.icon}
                                                {config?.label ?? key}
                                            </Tag>
                                        </Tooltip>
                                    );
                                })}
                            </AddonTagsWrapper>
                        )}
                        <JobAlert isActive={isJobActive}/>
                    </JobMetaWrapper>
                    <ButtonGroupMessageItem>
                        {isJobActive && (
                            <BtnStyledMessageItem type="primary" size="small" onClick={() => publishMutation.mutate(3)}
                                                  loading={publishMutation.isPending}
                                                  icon={<StopOutlined />}>
                                Deaktivieren
                            </BtnStyledMessageItem>
                        )}
                        {!isJobActive && (
                            <BtnStyledMessageItem
                                type="primary"
                                size="small"
                                loading={canPublish && publishMutation.isPending}
                                icon={<CheckCircleOutlined />}
                                onClick={() => {
                                    if (canPublish) {
                                        publishMutation.mutate(4);
                                    } else {
                                        navigate(`/new/job?draftId=${item.id}`);
                                    }
                                }}
                            >
                                Aktivieren
                            </BtnStyledMessageItem>
                        )}
                        <BtnStyledMessageItem size="small" onClick={onEdit} icon={<EditOutlined />}>Stelle bearbeiten</BtnStyledMessageItem>
                        <BtnStyledMessageItem size="small" onClick={handleDuplicate}
                                              loading={duplicateMutation.isPending}
                                              icon={<CopyOutlined />}>Duplizieren</BtnStyledMessageItem>
                        <BtnStyledMessageItem size="small" danger onClick={handleDelete}
                                              loading={deleteMutation.isPending}
                                              icon={<DeleteOutlined />}>Löschen</BtnStyledMessageItem>
                    </ButtonGroupMessageItem>
                </ItemDetails>
            </BgWrapper>
        </RowItemWrapper>
    );
});

const JobAlert: FC<JobAlertProps> = ({isActive, pricingModel}) => {
    if (pricingModel === 'per_job') {
        return (
            <Alert
                message={isActive
                    ? 'Ihre Stellenanzeige ist aktiv und wird passenden Kandidaten automatisch vorgestellt.'
                    : 'Stellenanzeige ist nicht aktiv. Verwalten Sie Ihr Abonnement unter "Abo verwalten".'
                }
                type={isActive ? "success" : "warning"}
                showIcon
                icon={isActive ? <CheckCircleOutlined/> : <QuestionCircleOutlined/>}
            />
        );
    }

    return (
        <Alert
            message={isActive ? 'Ihre Stellenanzeige ist veröffentlicht und wird passenden Kandidaten automatisch vorgestellt.' : 'Bitte veröffentlichen Sie Ihre Stellenanzeige über "Stelle bearbeiten", um Bewerbungen zu erhalten.'}
            type="info"
            showIcon
            icon={isActive ? <LineChartOutlined/> : <QuestionCircleOutlined/>}
        />
    );
};

const JobTitle = styled.p`
    margin: 0;
    font-weight: bold;
    font-size: 16px;
`;

const JobLocation = styled.p`
    margin: 0;
    font-size: 14px;
    font-weight: 300;
    color: #424242;
`;

const JobMetaWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const AddonTagsWrapper = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
`;

